import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const SpecialCourses: React.FC = () => {
  const [activeCard, setActiveCard] = useState<number | null>(null);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const courses = [
    {
      title: "Japanese Etiquette",
      description: "Master Japanese  etiquette and communication skills",
      icon: "💼",
      link: "/services/business-japanese",
      features: [
        "Business terminology",
        "Email writing",
        "Meeting etiquette",
        "Presentation skills"
      ],
      bgPattern: "radial-gradient(circle at 10% 20%, rgb(255, 126, 95) 0%, rgb(254, 180, 123) 100.2%)"
    },
    {
      title: "Japanese Culture",
      description: "Deep dive into Japanese traditions, customs, and modern culture",
      icon: "🎎",
      link: "/services/japanese-culture",
      features: [
        "Traditional arts",
        "Modern pop culture",
        "Social customs",
        "Cultural festivals"
      ],
      bgPattern: "radial-gradient(circle at 10% 20%, rgb(111, 123, 247) 0%, rgb(72, 149, 239) 100.2%)"
    },
    {
      title: "Conversation Practice",
      description: "Improve your speaking skills with native Japanese speakers",
      icon: "🗣️",
      link: "/services/conversation-practice",
      features: [
        "Daily conversations",
        "Pronunciation drills",
        "Speaking confidence",
        "Real-life scenarios"
      ],
      bgPattern: "radial-gradient(circle at 10% 20%, rgb(186, 116, 247) 0%, rgb(239, 72, 172) 100.2%)"
    }
  ];

  return (
    <div className="min-h-screen pt-24 relative overflow-hidden bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-50 via-gray-100 to-gray-200">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {Array.from({ length: 30 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full mix-blend-multiply filter blur-xl opacity-20"
            style={{
              width: 100 + Math.random() * 300,
              height: 100 + Math.random() * 300,
              background: i % 3 === 0 
                ? 'rgba(59, 130, 246, 0.2)' 
                : i % 3 === 1 
                  ? 'rgba(139, 92, 246, 0.2)' 
                  : 'rgba(236, 72, 153, 0.2)',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`
            }}
            animate={{
              x: [0, Math.random() * 100 - 50],
              y: [0, Math.random() * 100 - 50],
              opacity: [0.2, 0.4, 0.2]
            }}
            transition={{
              duration: 15 + Math.random() * 15,
              repeat: Infinity,
              repeatType: 'reverse',
              ease: 'easeInOut',
              delay: Math.random() * 5
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="text-center mb-32"
        >
          <motion.div 
            className="inline-block mb-6"
            animate={{ 
              y: [0, -10, 0]
            }}
            transition={{ 
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <h2 className="text-6xl md:text-7xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary drop-shadow-lg">
              Special Courses
            </h2>
          </motion.div>
          <motion.div
            className="h-1 w-24 bg-gradient-to-r from-primary to-secondary mx-auto mb-6 rounded-full"
            initial={{ width: 0 }}
            whileInView={{ width: 96 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.5 }}
          />
          <motion.p 
            className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto font-medium"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.7 }}
          >
            Enhance your Japanese language skills with our specialized courses designed for specific needs.
          </motion.p>
        </motion.div>

        {/* Courses Section */}
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 mb-32">
          {courses.map((course, index) => (
            <motion.div
              key={course.title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="relative"
              onHoverStart={() => setHoveredCard(index)}
              onHoverEnd={() => setHoveredCard(null)}
            >
              <motion.div
                className="absolute inset-0 rounded-3xl"
                style={{
                  background: course.bgPattern,
                  opacity: hoveredCard === index ? 0.2 : 0.1,
                }}
                animate={{
                  scale: hoveredCard === index ? 1.02 : 1,
                }}
                transition={{ duration: 0.3 }}
              />
              <motion.div
                className="relative p-8 rounded-3xl bg-white/95 backdrop-blur-sm shadow-xl border border-gray-100 overflow-hidden"
                whileHover={{ y: -10 }}
              >
                <motion.div 
                  className="text-6xl mb-8"
                  animate={hoveredCard === index ? {
                    scale: [1, 1.2, 1],
                    y: [0, -10, 0]
                  } : {}}
                  transition={{ duration: 0.5 }}
                >
                  {course.icon}
                </motion.div>
                <h3 className="text-3xl font-bold mb-4 text-gray-900">{course.title}</h3>
                <p className="text-lg text-gray-600 mb-8">{course.description}</p>
                
                <div className="space-y-4 mb-8">
                  {course.features.map((feature, i) => (
                    <motion.div
                      key={i}
                      className="flex items-center text-gray-700"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <motion.div
                        className="w-2 h-2 rounded-full bg-gradient-to-r from-primary to-secondary mr-4"
                        animate={{
                          scale: [1, 1.5, 1],
                          opacity: [0.5, 1, 0.5]
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: i * 0.2
                        }}
                      />
                      <span>{feature}</span>
                    </motion.div>
                  ))}
                </div>

                {/* <Link to={course.link}>
                  <motion.div
                    className="inline-flex items-center text-gray-900 font-semibold group"
                    whileHover={{ x: 5 }}
                  >
                    Learn More
                    <motion.svg 
                      className="w-6 h-6 ml-2 group-hover:text-primary"
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                      animate={{ 
                        x: [0, 5, 0]
                      }}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </motion.svg>
                  </motion.div>
                </Link> */}
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mt-32"
        >
          <Link to="/contact">
            <motion.button
              className="px-12 py-6 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white text-xl font-bold rounded-full relative overflow-hidden group mb-4"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.span
                className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"
                animate={{
                  x: ['-100%', '100%']
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
              Start Learning Today
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default SpecialCourses; 